import React, { useState } from 'react';
import { Camera, Calendar, ChevronDown, User, MapPin, Briefcase, FileText, Search } from 'lucide-react';

export default function TourGuideRegistration() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    nicNumber: '',
    passportNumber: '',
    contactNumber: '',
    emailAddress: '',
    postalAddress: '',
    registrationNumber: '',
    category: '',
    registeredDate: '',
    licenseExpiry: '',
    areasServe: [],
    destinationsServe: [],
    supportingLanguages: [],
    specialNotes: ''
  });

  const [dropdownOpen, setDropdownOpen] = useState({
    areasServe: false,
    destinationsServe: false,
    supportingLanguages: false
  });

  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        alert('Please select a valid image file (JPG, JPEG, or PNG)');
        return;
      }
      
      const maxSize = 4 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('File size must be less than 4MB');
        return;
      }

      setProfileImage(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMultiSelect = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: prev[fieldName].includes(value) 
        ? prev[fieldName].filter(item => item !== value)
        : [...prev[fieldName], value]
    }));
  };

  const toggleDropdown = (fieldName) => {
    setDropdownOpen(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  const areas = [
    { value: 'western', label: 'Western Province' },
    { value: 'central', label: 'Central Province' },
    { value: 'southern', label: 'Southern Province' },
    { value: 'northern', label: 'Northern Province' },
    { value: 'eastern', label: 'Eastern Province' },
    { value: 'northwestern', label: 'North Western Province' },
    { value: 'northcentral', label: 'North Central Province' },
    { value: 'uva', label: 'Uva Province' },
    { value: 'sabaragamuwa', label: 'Sabaragamuwa Province' }
  ];

  const destinations = [
    { value: 'colombo', label: 'Colombo' },
    { value: 'kandy', label: 'Kandy' },
    { value: 'galle', label: 'Galle' },
    { value: 'anuradhapura', label: 'Anuradhapura' },
    { value: 'polonnaruwa', label: 'Polonnaruwa' },
    { value: 'sigiriya', label: 'Sigiriya' },
    { value: 'ella', label: 'Ella' },
    { value: 'nuwara-eliya', label: 'Nuwara Eliya' }
  ];

  const languages = [
    { value: 'english', label: 'English' },
    { value: 'sinhala', label: 'Sinhala' },
    { value: 'tamil', label: 'Tamil' },
    { value: 'german', label: 'German' },
    { value: 'french', label: 'French' },
    { value: 'spanish', label: 'Spanish' },
    { value: 'italian', label: 'Italian' },
    { value: 'japanese', label: 'Japanese' },
    { value: 'chinese', label: 'Chinese' },
    { value: 'russian', label: 'Russian' }
  ];

  const MultiSelectDropdown = ({ fieldName, options, placeholder, selected }) => (
    <div className="relative">
      <button
        type="button"
        onClick={() => toggleDropdown(fieldName)}
        className="appearance-none text-sm bg-white border border-slate-300 rounded-lg pl-3 pr-8 py-2.5 w-full text-left focus:outline-none focus:ring-2 focus:ring-brand-blue/30 hover:bg-slate-50 transition-colors"
      >
        <span className={selected.length > 0 ? "text-slate-700" : "text-slate-400"}>
          {selected.length > 0 ? `${selected.length} selected` : placeholder}
        </span>
        <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${dropdownOpen[fieldName] ? 'rotate-180' : ''}`} />
        </div>
      </button>
      
      {dropdownOpen[fieldName] && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-sm max-h-48 overflow-y-auto">
          {options.map((option) => (
            <label
              key={option.value}
              className="flex items-center px-3 py-2 text-xs hover:bg-slate-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                onChange={() => handleMultiSelect(fieldName, option.value)}
                className="mr-2 h-3 w-3 text-brand-blue border-slate-300 rounded focus:ring-brand-blue"
              />
              <span className="text-slate-700">{option.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );

  const Card = ({ icon: Icon, title, children, className = "" }) => (
    <div className={`bg-white rounded-xl border border-slate-200 ${className}`}>
      <div className="border-b border-slate-200 px-6 py-4">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-brand-blue" />
          <h2 className="text-sm font-bold text-slate-900">{title}</h2>
        </div>
      </div>
      <div className="p-6">
        {children}
      </div>
    </div>
  );

  return (
    <div>
      <div className="w-full">
        {/* Page Title */}
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-slate-900">Tour Guide Register</h1>
          <p className="mt-1 text-sm text-slate-500">Complete the form below to register a new tour guide</p>
        </div>

        <div className="space-y-4">
          {/* Profile & Personal Information Card */}
          <Card icon={User} title="Personal Information">
            <div className="bg-slate-50 rounded-lg border-2 border-dashed border-slate-200 p-6 mb-6 hover:border-brand-blue/50 transition-colors">
              <div className="flex flex-col items-center">
                <div className="relative mb-4">
                  <div className="w-32 h-32 rounded-full bg-white border-4 border-white shadow-sm flex items-center justify-center overflow-hidden">
                    {imagePreview ? (
                      <img 
                        src={imagePreview} 
                        alt="Profile Preview" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex flex-col items-center">
                        <Camera className="h-12 w-12 text-slate-400 mb-2" />
                        <span className="text-xs text-slate-500 font-medium">Profile Photo</span>
                      </div>
                    )}
                  </div>
                  {imagePreview && (
                    <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
                
                <div className="text-center">
                  <button 
                    type="button" 
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-brand-blue bg-white border border-brand-blue/30 rounded-lg hover:bg-brand-blue/5 hover:border-brand-blue/50 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:ring-offset-2"
                    onClick={() => document.querySelector('input[type="file"]').click()}
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    {imagePreview ? 'Change Photo' : 'Upload Photo'}
                  </button>
                  
                  <div className="mt-3 text-xs text-slate-500 space-y-1">
                    <p className="font-medium">Requirements:</p>
                    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
                      <span>• Max size: 4MB</span>
                      <span>• Format: JPG, PNG</span>
                      <span>• Square ratio preferred</span>
                    </div>
                    {profileImage && (
                      <div className="flex items-center justify-center mt-2 text-green-600 font-medium">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {profileImage.name}
                      </div>
                    )}
                  </div>
                </div>
                
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Enter first name"
                  className="w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Enter last name"
                  className="w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">
                  NIC Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nicNumber"
                  value={formData.nicNumber}
                  onChange={handleInputChange}
                  placeholder="Enter NIC number"
                  className="w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">
                  Passport Number
                </label>
                <input
                  type="text"
                  name="passportNumber"
                  value={formData.passportNumber}
                  onChange={handleInputChange}
                  placeholder="Enter passport number"
                  className="w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">
                  Contact Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleInputChange}
                  placeholder="+94 XX XXX XXXX"
                  className="w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="emailAddress"
                  value={formData.emailAddress}
                  onChange={handleInputChange}
                  placeholder="Enter email address"
                  className="w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                />
              </div>
            </div>
          </Card>

          {/* Address Information Card */}
          <Card icon={MapPin} title="Address Information">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">
                Postal Address <span className="text-red-500">*</span>
              </label>
              <textarea
                name="postalAddress"
                value={formData.postalAddress}
                onChange={handleInputChange}
                placeholder="Enter complete postal address"
                rows={4}
                className="w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
              />
            </div>
          </Card>

          {/* Professional Information Card */}
          <Card icon={Briefcase} title="Professional Information">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">
                  Registration Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="registrationNumber"
                  value={formData.registrationNumber}
                  onChange={handleInputChange}
                  placeholder="TG-YYYY-XXXX"
                  className="w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">
                  Category <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="appearance-none w-full px-3 py-2.5 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                  >
                    <option value="">Select category</option>
                    <option value="national">National Guide</option>
                    <option value="regional">Regional Guide</option>
                    <option value="local">Local Guide</option>
                    <option value="specialized">Specialized Guide</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  </div>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">
                  Registered Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="registeredDate"
                  value={formData.registeredDate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">
                  License Expiry <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="licenseExpiry"
                  value={formData.licenseExpiry}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                />
              </div>
            </div>
          </Card>

          {/* Service Areas & Languages Card */}
          <Card icon={MapPin} title="Service Areas & Languages">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">
                  Areas Serve <span className="text-red-500">*</span>
                </label>
                <MultiSelectDropdown
                  fieldName="areasServe"
                  options={areas}
                  placeholder="Select areas (multiple selection)"
                  selected={formData.areasServe}
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">
                  Destinations Serve <span className="text-red-500">*</span>
                </label>
                <MultiSelectDropdown
                  fieldName="destinationsServe"
                  options={destinations}
                  placeholder="Select destinations (multiple selection)"
                  selected={formData.destinationsServe}
                />
              </div>
            </div>

            <div className="space-y-1 mt-4">
              <label className="text-xs font-semibold text-slate-700">
                Supporting Languages <span className="text-red-500">*</span>
              </label>
              <MultiSelectDropdown
                fieldName="supportingLanguages"
                options={languages}
                placeholder="Select languages (multiple selection)"
                selected={formData.supportingLanguages}
              />
            </div>
          </Card>

          {/* Additional Information Card */}
          <Card icon={FileText} title="Additional Information">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">
                Special Notes/Qualifications
              </label>
              <textarea
                name="specialNotes"
                value={formData.specialNotes}
                onChange={handleInputChange}
                placeholder="Enter any special qualifications, certifications or notes"
                rows={4}
                className="w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
              />
            </div>
          </Card>
        </div>

        {/* Footer Actions */}
        <div className="p-6 mt-4 bg-white border rounded-xl border-slate-200">
          <div className="mb-4">
            <div className="space-y-1 text-xs text-slate-500">
              <p>&bull; Please ensure all required fields (*) are completed before saving.</p>
              <p>&bull; The tour guide will be notified via email once the registration is approved.</p>
              <p>&bull; All information provided will be verified before final approval.</p>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              className="px-4 py-2 text-sm font-semibold transition-colors bg-white border rounded-lg border-slate-300 text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-semibold text-white transition-colors rounded-lg bg-brand-blue hover:bg-brand-blue-dark"
            >
              Save Guide Registration
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}